import { useEffect, useState } from 'react'
import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
} from '@tanstack/react-router'
import { Layout } from './components/Layout'
import { useAuth } from './auth/AuthProvider'
import { useSetupState } from './api/hooks'
import { LoadingBlock } from './components/ui'
import { CaseDetailPage, CasesPage } from './pages/CasesPage'
import { DocumentDetailPage } from './pages/DocumentDetailPage'
import { DocumentsPage } from './pages/DocumentsPage'
import { LoginPage } from './pages/LoginPage'
import { OverviewPage } from './pages/OverviewPage'
import { QuarantinePage } from './pages/QuarantinePage'
import { RemindersPage } from './pages/RemindersPage'
import { SearchPage } from './pages/SearchPage'
import { SettingsPage } from './pages/SettingsPage'
import { SetupPage } from './pages/SetupPage'
import { UploadPage } from './pages/UploadPage'

/**
 * A single gate decides what the visitor sees: the first-run wizard while no user
 * exists, the login form while nobody is signed in, and the application otherwise.
 */
function Shell() {
  const { ready, signedIn } = useAuth()
  const setup = useSetupState()
  // The wizard keeps running after the admin exists: creating the account is only its
  // first step, so the gate cannot close the moment setup_required flips to false.
  const [wizardOpen, setWizardOpen] = useState(false)

  useEffect(() => {
    if (setup.data?.setup_required) setWizardOpen(true)
  }, [setup.data?.setup_required])

  if (!ready || setup.isPending) {
    return (
      <div className="flex min-h-full items-center justify-center p-8">
        <LoadingBlock label="Backoffice startet" />
      </div>
    )
  }
  if (wizardOpen) return <SetupPage onFinished={() => setWizardOpen(false)} />
  if (!signedIn) return <LoginPage />

  return (
    <Layout>
      <Outlet />
    </Layout>
  )
}

const rootRoute = createRootRoute({ component: Shell })

const routes = [
  createRoute({ getParentRoute: () => rootRoute, path: '/', component: OverviewPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/dokumente', component: DocumentsPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/dokumente/$documentId',
    component: DocumentDetailPage,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/vorgaenge', component: CasesPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/vorgaenge/$caseId',
    component: CaseDetailPage,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/suche', component: SearchPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/spam', component: QuarantinePage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/erinnerungen', component: RemindersPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/brief', component: UploadPage }),
  createRoute({ getParentRoute: () => rootRoute, path: '/einstellungen', component: SettingsPage }),
]

const routeTree = rootRoute.addChildren(routes)

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultNotFoundComponent: () => (
    <div className="p-8">
      <h1 className="text-2xl">Seite nicht gefunden</h1>
      <p className="mt-2 text-sm text-paper-500">Diese Adresse gibt es nicht.</p>
    </div>
  ),
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
